import { useEffect, useState } from 'react';
import { ReportHeader } from '@/components/reports/ReportUI';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, Package, TrendingDown, TrendingUp, AlertTriangle, Camera, Loader2, MessageCircle, BarChart3, Barcode, Printer, Download, FileDown, DollarSign, Bell, Settings, Trash2, Upload, Search, Link, X } from 'lucide-react';
import PageTour from '@/components/PageTour';
import { ExportButtons } from '@/components/ExportButtons';
import { WhatsAppSendDialog } from '@/components/WhatsAppSendDialog';
import { cur, RiyalIcon } from '@/lib/currency';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { format } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { useBranchStore } from '@/stores';
import { VirtualizedTable } from '@/components/VirtualizedTable';
import { localToday } from '@/lib/dateRange';
import { RecordWasteDialog, reasonLabel } from '@/components/stock/RecordWasteDialog';
import { BranchMultiSelect } from '@/components/BranchMultiSelect';

export default function StockPage() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const { branches, fetchBranches } = useBranchStore();
  const [suppliers, setSuppliers] = useState([]);
  const [balance, setBalance] = useState([]);
  const [entries, setEntries] = useState([]);
  // iter880 — Date range filter for Stock In Log + Usage Log tabs.
  const [stockDateRange, setStockDateRange] = useState({ start_date: null, end_date: null });
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branchFilter, setBranchFilter] = useState('');
  const [showAddItem, setShowAddItem] = useState(false);
  const [showStockIn, setShowStockIn] = useState(false);
  const [showScanDialog, setShowScanDialog] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [consumptionReport, setConsumptionReport] = useState(null);
  const [profitReport, setProfitReport] = useState(null);
  const [wastageReport, setWastageReport] = useState(null);
  const [showRecordWaste, setShowRecordWaste] = useState(false);
  const [wasteRange, setWasteRange] = useState({ start_date: null, end_date: null });
  const [wasteBranchIds, setWasteBranchIds] = useState([]);
  const loadWastage = async () => {
    try {
      const params = {};
      if (wasteRange.start_date && wasteRange.end_date) {
        params.start = wasteRange.start_date; params.end = wasteRange.end_date;
      } else {
        params.days = reportDays;
      }
      if (wasteBranchIds.length) params.branch_id = wasteBranchIds.join(',');
      const { data } = await api.get('/stock/report/wastage', { params });
      setWastageReport(data);
    } catch { toast.error('Failed to load wastage report'); }
  };
  const [foodProduction, setFoodProduction] = useState(null);
  const [prodStart, setProdStart] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); });
  const [prodEnd, setProdEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const loadProduction = async () => {
    try {
      const br = branchFilter || undefined;
      const { data } = await api.get('/stock/report/food-production', { params: { branch_id: br, start_date: prodStart, end_date: prodEnd } });
      setFoodProduction(data);
    } catch { toast.error('Failed to load production report'); }
  };
  const [reportDays, setReportDays] = useState(30);
  const [smartAlerts, setSmartAlerts] = useState({ alerts: [], summary: {} });
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [showBarcodePreview, setShowBarcodePreview] = useState(false);
  const [selectedBarcodeItem, setSelectedBarcodeItem] = useState(null);
  const [barcodeImageUrl, setBarcodeImageUrl] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [selectedItemsForBatch, setSelectedItemsForBatch] = useState([]);
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  const [alertSettings, setAlertSettings] = useState({ global_threshold: 10, enabled: true, check_interval_minutes: 60, email_enabled: false });
  const [stockCategories, setStockCategories] = useState([]);
  const [stockUnits, setStockUnits] = useState([]);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewUnit, setShowNewUnit] = useState(false);
  const [newUnitName, setNewUnitName] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showMatchDialog, setShowMatchDialog] = useState(false);
  const [matchResults, setMatchResults] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importData, setImportData] = useState('');
  const [showSupplierNames, setShowSupplierNames] = useState(null);
  const [supplierMappings, setSupplierMappings] = useState([]);
  const [newMapping, setNewMapping] = useState({ supplier_id: '', supplier_item_name: '', supplier_item_code: '', supplier_price: '' });

  const [newItem, setNewItem] = useState({ name: '', cost_price: '', unit_price: '', unit: 'piece', category: '', min_stock_level: '', reorder_quantity: '', auto_reorder: false, pack_units: [] });
  const [stockInData, setStockInData] = useState({ item_id: '', branch_id: '', quantity: '', unit_cost: '', supplier_id: '', date: localToday(), notes: '' });

  useEffect(() => { fetchAll(); fetchSmartAlerts(); fetchAlertSettings(); fetchCategoriesAndUnits(); }, []);
  // iter880 — re-fetch when the date range changes
  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [stockDateRange.start_date, stockDateRange.end_date]);
  // KPI "Stock Entries" = server total for the selected branch + date range (the list itself is paged).
  const [entriesTotal, setEntriesTotal] = useState(null);
  useEffect(() => {
    const params = { limit: 1, ...(branchFilter ? { branch_id: branchFilter } : {}),
      ...(stockDateRange.start_date ? { start_date: stockDateRange.start_date } : {}), ...(stockDateRange.end_date ? { end_date: stockDateRange.end_date } : {}) };
    api.get('/stock/entries', { params }).then((r) => setEntriesTotal(r.data?.total ?? null)).catch(() => setEntriesTotal(null));
  }, [branchFilter, stockDateRange.start_date, stockDateRange.end_date]);
  useEffect(() => { fetchBalance(); fetchSmartAlerts(); }, [branchFilter]);
  useEffect(() => { if (wastageReport) loadWastage(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [wasteRange.start_date, wasteRange.end_date, wasteBranchIds]);
  const fetchAll = async () => {
    try {
      fetchBranches();
      // iter880 — pass date range if set (server may filter; client filters below as safety net)
      const params = {};
      if (stockDateRange.start_date) params.start_date = stockDateRange.start_date;
      if (stockDateRange.end_date) params.end_date = stockDateRange.end_date;
      const [iR, sR, eR, uR] = await Promise.all([
        api.get('/items'), api.get('/suppliers'),
        api.get('/stock/entries', { params }), api.get('/stock/usage', { params })
      ]);
      setItems(iR.data); setSuppliers(sR.data);
      setEntries(eR.data?.data || eR.data); setUsage(uR.data?.data || uR.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const fetchAlertSettings = async () => {
    try { const res = await api.get('/scheduler/stock-alert-settings'); setAlertSettings(res.data); } catch {}
  };
  const saveAlertSettings = async () => {
    try { await api.post('/scheduler/stock-alert-settings', alertSettings); toast.success('Alert settings saved'); setShowAlertSettings(false); } catch { toast.error('Failed to save'); }
  };
  const triggerStockCheck = async () => {
    try { const res = await api.post('/scheduler/stock-alerts/check'); toast.success(res.data.message); } catch { toast.error('Failed to check stock'); }
  };


  const fetchBalance = async () => {
    try {
      const url = branchFilter ? `/stock/balance?branch_id=${branchFilter}` : '/stock/balance';
      const res = await api.get(url);
      setBalance(res.data);
    } catch {}
  };

  const fetchCategoriesAndUnits = async () => {
    try {
      const [catRes, unitRes] = await Promise.allSettled([api.get('/stock/categories'), api.get('/stock/units')]);
      if (catRes.status === 'fulfilled') setStockCategories(catRes.value.data || []);
      if (unitRes.status === 'fulfilled') setStockUnits(unitRes.value.data || []);
    } catch {}
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await api.post('/stock/categories', { name: newCategoryName.trim() });
      toast.success('Category added');
      setNewCategoryName(''); setShowNewCategory(false);
      fetchCategoriesAndUnits();
    } catch { toast.error('Failed'); }
  };

  const handleAddUnit = async () => {
    if (!newUnitName.trim()) return;
    try {
      await api.post('/stock/units', { name: newUnitName.trim() });
      toast.success('Unit added');
      setNewUnitName(''); setShowNewUnit(false);
      fetchCategoriesAndUnits();
    } catch { toast.error('Failed'); }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this item? This cannot be undone.')) return;
    try {
      await api.delete(`/items/${itemId}`);
      toast.success('Item deleted');
      fetchAll(); fetchBalance();
    } catch { toast.error('Failed to delete'); }
  };

  const handleSmartMatch = async () => {
    if (!scanResult?.items?.length) return;
    try {
      const res = await api.post('/stock/match-with-supplier-names', { 
        items: scanResult.items,
        supplier_id: scanResult.supplier_id || ''
      });
      setMatchResults(res.data);
      setShowMatchDialog(true);
    } catch { toast.error('Matching failed'); }
  };

  const handleImportCSV = async () => {
    try {
      const lines = importData.trim().split('\n');
      const items = lines.map(line => {
        const parts = line.split(',').map(s => s.trim());
        return { name: parts[0], category: parts[1] || '', unit: parts[2] || 'pc', buying_price: parseFloat(parts[3]) || 0, selling_price: parseFloat(parts[4]) || 0, min_stock_level: parseFloat(parts[5]) || 0 };
      }).filter(i => i.name);
      const res = await api.post('/stock/import', { items });
      toast.success(`Created: ${res.data.created}, Updated: ${res.data.updated}`);
      setImportData(''); setShowImport(false);
      fetchAll();
    } catch { toast.error('Import failed'); }
  };

  const openSupplierNames = async (item) => {
    setShowSupplierNames(item);
    try {
      const res = await api.get(`/stock/supplier-mappings?item_id=${item.id}`);
      setSupplierMappings(res.data || []);
    } catch { setSupplierMappings([]); }
    setNewMapping({ supplier_id: '', supplier_item_name: '', supplier_item_code: '', supplier_price: '' });
  };

  const addSupplierMapping = async () => {
    if (!newMapping.supplier_id || !newMapping.supplier_item_name) { toast.error('Supplier and name required'); return; }
    try {
      const supplier = suppliers.find(s => s.id === newMapping.supplier_id);
      await api.post('/stock/supplier-mappings', {
        item_id: showSupplierNames.id, item_name: showSupplierNames.name,
        supplier_id: newMapping.supplier_id, supplier_name: supplier?.name || '',
        supplier_item_name: newMapping.supplier_item_name,
        supplier_item_code: newMapping.supplier_item_code,
        supplier_price: parseFloat(newMapping.supplier_price) || 0,
      });
      toast.success('Supplier name added');
      openSupplierNames(showSupplierNames);
    } catch { toast.error('Failed'); }
  };

  const deleteSupplierMapping = async (mappingId) => {
    try {
      await api.delete(`/stock/supplier-mappings/${mappingId}`);
      toast.success('Removed');
      openSupplierNames(showSupplierNames);
    } catch { toast.error('Failed'); }
  };

  // suppliers already fetched in fetchAll()

  const fetchSmartAlerts = async () => {
    setAlertsLoading(true);
    try {
      const url = branchFilter 
        ? `/stock/smart-alerts?branch_id=${branchFilter}&days_lookback=30&days_forecast=7` 
        : '/stock/smart-alerts?days_lookback=30&days_forecast=7';
      const res = await api.get(url);
      setSmartAlerts(res.data);
    } catch (err) {
      console.error('Failed to fetch smart alerts:', err);
    } finally {
      setAlertsLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.name) { toast.error('Name required'); return; }
    try {
      await api.post('/items', {
        ...newItem,
        cost_price: parseFloat(newItem.cost_price) || 0,
        unit_price: parseFloat(newItem.unit_price) || 0,
        min_stock_level: parseFloat(newItem.min_stock_level) || 0,
        reorder_quantity: parseFloat(newItem.reorder_quantity) || 0,
        auto_reorder: newItem.auto_reorder || false,
        pack_units: (newItem.pack_units || []).filter(p => p.name && parseFloat(p.factor_to_base) > 0)
          .map(p => ({ name: p.name.trim(), factor_to_base: parseFloat(p.factor_to_base) })),
      });
      toast.success('Item added');
      setNewItem({ name: '', cost_price: '', unit_price: '', unit: 'piece', category: '', min_stock_level: '', reorder_quantity: '', auto_reorder: false, pack_units: [] });
      setShowAddItem(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleStockIn = async () => {
    if (!stockInData.item_id || !stockInData.branch_id || !stockInData.quantity) {
      toast.error('Select item, branch and quantity'); return;
    }
    try {
      await api.post('/stock/entries', {
        ...stockInData,
        quantity: parseFloat(stockInData.quantity),
        unit_cost: parseFloat(stockInData.unit_cost) || 0,
        supplier_id: stockInData.supplier_id || null,
        date: new Date(stockInData.date).toISOString()
      });
      toast.success('Stock added');
      setStockInData({ item_id: '', branch_id: '', quantity: '', unit_cost: '', supplier_id: '', date: localToday(), notes: '' });
      setShowStockIn(false);
      fetchAll(); fetchBalance();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleScanInvoice = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setScanning(true);
    setScanResult(null);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        try {
          const res = await api.post('/stock/scan-invoice', { image: base64 });
          setScanResult(res.data);
          toast.success('Invoice scanned successfully');
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Scan failed');
        }
        setScanning(false);
      };
      reader.readAsDataURL(file);
    } catch { setScanning(false); toast.error('Failed to read file'); }
  };

  const handleImportScanned = async () => {
    if (!scanResult?.items?.length || !stockInData.branch_id) {
      toast.error('Select a branch first'); return;
    }
    try {
      await api.post('/stock/entries/bulk', {
        branch_id: stockInData.branch_id,
        supplier_id: stockInData.supplier_id || null,
        source: 'invoice_scan',
        date: new Date(stockInData.date).toISOString(),
        items: scanResult.items.map(i => ({
          item_name: i.name,
          quantity: i.quantity || 1,
          unit_cost: i.unit_cost || 0,
          unit: i.unit || 'piece'
        }))
      });
      toast.success(`${scanResult.items.length} items added to stock`);
      setScanResult(null);
      setShowScanDialog(false);
      fetchAll(); fetchBalance();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // Barcode functions
  const handlePreviewBarcode = async (item) => {
    setSelectedBarcodeItem(item);
    setBarcodeLoading(true);
    setShowBarcodePreview(true);
    try {
      const response = await api.get(`/barcode/item/${item.id}/preview`, { responseType: 'blob' });
      const imageUrl = URL.createObjectURL(response.data);
      setBarcodeImageUrl(imageUrl);
    } catch (err) {
      toast.error('Failed to generate barcode');
      setShowBarcodePreview(false);
    } finally {
      setBarcodeLoading(false);
    }
  };

  const handleDownloadBarcode = async (itemId) => {
    try {
      const response = await api.get(`/barcode/item/${itemId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `barcode_${itemId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Barcode downloaded');
    } catch (err) {
      toast.error('Failed to download barcode');
    }
  };

  const handlePrintBarcode = async () => {
    if (!barcodeImageUrl) return;
    // iter892 — Route via printSummary so mobile popup-blockers don't kill
    // the print (window.open('','_blank') was silently blocked on iOS/Chrome
    // mobile) and so Android tablets with a configured thermal printer can
    // print via the native bridge in the future. Barcode images can't be
    // rendered via ESC/POS text so today's path is: printers -> iframe fallback.
    const htmlFallback = `
      <html>
        <head><title>Print Barcode</title></head>
        <body style="margin: 0; padding: 20px; display: flex; flex-direction: column; align-items: center;">
          <img src="${barcodeImageUrl}" style="max-width: 100%;" />
          <div style="text-align:center;border-top:1px solid #eee;margin-top:16px;padding-top:8px;font-size:9px;color:#999;font-family:Arial,sans-serif;">Powered by <b>SMARTO</b></div>
          <script>window.onload = function() { setTimeout(function(){ window.print(); }, 150); }</script>
        </body>
      </html>
    `;
    let printers = [];
    try {
      const { data } = await api.get('/printers');
      printers = Array.isArray(data) ? data : (data?.items || []);
    } catch { /* no printers — fallback works */ }
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const { printSummary } = await import('@/lib/printSummary');
    await printSummary({
      printers,
      branchId: user?.branch_id,
      title: 'Barcode',
      htmlFallback,
    });
  };

  const handleBatchPrint = async () => {
    if (selectedItemsForBatch.length === 0) {
      toast.error('Select items to print barcodes');
      return;
    }
    try {
      const response = await api.post('/barcode/batch', 
        { item_ids: selectedItemsForBatch, labels_per_item: 1 },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(response.data);
      window.open(url, '_blank');
      toast.success('Batch barcodes generated');
    } catch (err) {
      toast.error('Failed to generate batch barcodes');
    }
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItemsForBatch(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64">Loading...</div></DashboardLayout>;

  // Valuation basis: each branch's own weighted average cost (same as the single-branch stock ledger view).
  // All Branches = sum of branch values; a blended company-wide average is shown only for reference.
  const rowValue = (b) => (b.by_branch ? b.by_branch.reduce((t, x) => t + (x.value || 0), 0) : b.avg_cost * b.balance);
  const blendedValue = balance.reduce((s, b) => s + (b.avg_cost * b.balance), 0);
  const totalValue = !branchFilter && balance.some((b) => b.by_branch)
    ? balance.reduce((s, b) => s + rowValue(b), 0)
    : blendedValue;
  const lowStockCount = balance.filter(b => b.low_stock).length;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3">
          <ReportHeader icon={Package} title={<span data-testid="stock-title">{t('stock_title')}</span>} subtitle={t('stock_subtitle')} testId="stock-header" />
          <div className="flex gap-2 flex-wrap items-center">
            {/* iter880 — Shared date range for Stock In / Usage logs. */}
            <DateRangeFilter value={stockDateRange} onChange={setStockDateRange} testid="stock-date-range" />
            <Select value={branchFilter || "all"} onValueChange={(v) => setBranchFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="h-8 w-52 text-xs rounded-lg" data-testid="stock-branch-filter" title="Stock is held per branch — pick one branch to see its own quantities and values"><SelectValue placeholder={t('all_branches')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all_branches')} (company total)</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="rounded-xl" onClick={() => setShowWhatsApp(true)} data-testid="stock-whatsapp-btn">
              <MessageCircle size={14} className="mr-1" />WhatsApp
            </Button>
            <Button size="sm" variant="outline" className="rounded-xl" onClick={() => setShowScanDialog(true)} data-testid="scan-invoice-btn">
              <Camera size={14} className="mr-1" />{t('scan_invoice')}
            </Button>
            <Button size="sm" variant="outline" className="rounded-xl" onClick={() => setShowStockIn(true)} data-testid="stock-in-btn">
              <TrendingUp size={14} className="mr-1" />{t('stock_in')}
            </Button>
            <Button size="sm" className="rounded-xl" onClick={() => setShowAddItem(true)} data-testid="add-item-btn">
              <Plus size={14} className="mr-1" />{t('add_item')}
            </Button>
            <PageTour storageKey="tour_stock" steps={[
              { target: '[data-testid="add-item-btn-2"]', title: 'Add Stock Item', content: 'Create inventory items with cost price, selling price, min stock level, and auto-reorder settings.' },
              { target: '[data-testid="stock-in-btn-2"]', title: 'Stock In', content: 'Record incoming stock from suppliers or transfers.' },
              { target: '[data-testid="check-auto-reorder-btn"]', title: 'Auto-Reorder', content: 'Auto-reorder creates draft POs when stock falls below threshold. Enable per item and click Check Now to trigger.' },
            ]} />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-border hover:shadow-md transition-shadow">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('items')}</p>
                  <p className="text-3xl font-bold font-outfit text-foreground mt-1" data-testid="total-items">{balance.length}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-info/10 text-info flex items-center justify-center ring-1 ring-blue-100"><Package size={16} /></div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border hover:shadow-md transition-shadow">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('stock_value')}</p>
                  <p className="text-3xl font-bold font-outfit text-success mt-1" data-testid="stock-value-kpi">{cur()} {totalValue.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-success/10 text-success flex items-center justify-center ring-1 ring-emerald-100"><DollarSign size={16} /></div>
              </div>
            </CardContent>
          </Card>
          <Card className={`hover:shadow-md transition-shadow ${lowStockCount > 0 ? 'border-error/30 bg-error/10' : 'border-border'}`}>
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-[11px] font-semibold uppercase tracking-wider ${lowStockCount > 0 ? 'text-error' : 'text-muted-foreground'}`}>{t('min_stock')}</p>
                  <p className="text-3xl font-bold font-outfit text-error mt-1" data-testid="low-stock-count">{lowStockCount}</p>
                </div>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ring-1 ${lowStockCount > 0 ? 'bg-error/20 text-error ring-red-200' : 'bg-muted/50 text-muted-foreground ring-border'}`}><AlertTriangle size={16} /></div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border hover:shadow-md transition-shadow">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Stock Entries</p>
                  <p className="text-3xl font-bold font-outfit text-foreground mt-1" data-testid="stock-entries-kpi">{entriesTotal ?? entries.length}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center ring-1 ring-violet-100"><Package size={16} /></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alert Settings & Import/Export */}
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowAlertSettings(true)} data-testid="stock-alert-settings-btn">
            <Bell size={14} /> Alert Settings
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={triggerStockCheck} data-testid="stock-check-now-btn">
            <AlertTriangle size={14} /> Check Stock Now
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowImport(true)} data-testid="stock-import-btn">
            <Upload size={14} /> Import
          </Button>
          <Button size="sm" className="gap-1.5 bg-error hover:bg-error/90 text-white" onClick={() => setShowRecordWaste(true)} data-testid="stock-record-waste-btn">
            <Trash2 size={14} /> Record Waste
          </Button>
          <ExportButtons branchIds={branchFilter && branchFilter !== 'all' ? [branchFilter] : []} dataType="items" label="Stock Items" />
        </div>

        {/* Category Filter */}
        {stockCategories.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            <Button size="sm" variant={categoryFilter === 'all' ? 'default' : 'outline'} className="rounded-full text-xs h-7" onClick={() => setCategoryFilter('all')}>All</Button>
            {stockCategories.map(c => (
              <Button key={c.id} size="sm" variant={categoryFilter === c.name ? 'default' : 'outline'} className="rounded-full text-xs h-7" onClick={() => setCategoryFilter(c.name)}>{c.name}</Button>
            ))}
          </div>
        )}

        <Tabs defaultValue="balance">
          <TabsList className="max-w-full overflow-x-auto justify-start">
            <TabsTrigger value="balance">Current Stock</TabsTrigger>
            <TabsTrigger value="alerts" className="data-[state=active]:bg-error data-[state=active]:text-white">
              <AlertTriangle size={14} className="mr-1" />
              Smart Alerts {smartAlerts.summary?.critical > 0 && `(${smartAlerts.summary.critical})`}
            </TabsTrigger>
            <TabsTrigger value="entries">Stock In Log ({entries.length})</TabsTrigger>
            <TabsTrigger value="usage">Usage Log ({usage.length})</TabsTrigger>
            <TabsTrigger value="items">Item Master ({items.length})</TabsTrigger>
            <TabsTrigger value="reports" data-testid="stock-reports-tab" onClick={async () => {
              try {
                const br = branchFilter || undefined;
                const [cR, pR] = await Promise.all([
                  api.get('/stock/report/consumption', { params: { days: reportDays, branch_id: br } }),
                  api.get('/stock/report/profitability', { params: { branch_id: br } }),
                ]);
                setConsumptionReport(cR.data); setProfitReport(pR.data);
                loadWastage();
              } catch { toast.error('Failed to load reports'); }
            }}>Reports</TabsTrigger>
            <TabsTrigger value="production" data-testid="stock-production-tab" onClick={loadProduction}>Production & Variance</TabsTrigger>
          </TabsList>

          {/* SMART ALERTS */}
          <TabsContent value="alerts">
            <Card className="border-border">
              <CardContent className="pt-4">
                {alertsLoading ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="animate-spin mr-2" />
                    Analyzing stock velocity...
                  </div>
                ) : (
                  <>
                    {/* Alert Summary */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                      <div className="p-3 bg-error/10 rounded-xl text-center border border-error/30">
                        <p className="text-xs text-error">Critical</p>
                        <p className="text-2xl font-bold text-error">{smartAlerts.summary?.critical || 0}</p>
                      </div>
                      <div className="p-3 bg-warning/10 rounded-xl text-center border border-warning/30">
                        <p className="text-xs text-warning">Warning</p>
                        <p className="text-2xl font-bold text-warning">{smartAlerts.summary?.warning || 0}</p>
                      </div>
                      <div className="p-3 bg-info/10 rounded-xl text-center border border-info/30">
                        <p className="text-xs text-info">Info</p>
                        <p className="text-2xl font-bold text-info">{smartAlerts.summary?.info || 0}</p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-xl text-center border border-border">
                        <p className="text-xs text-muted-foreground">Total Alerts</p>
                        <p className="text-2xl font-bold text-foreground">{smartAlerts.summary?.total_alerts || 0}</p>
                      </div>
                    </div>
                    
                    <p className="text-xs text-muted-foreground mb-3">
                      Based on {smartAlerts.summary?.lookback_days || 30} days consumption, forecasting {smartAlerts.summary?.forecast_days || 7} days ahead
                    </p>

                    {/* Alert List */}
                    <div className="space-y-2">
                      {smartAlerts.alerts?.map(alert => (
                        <div 
                          key={alert.item_id} 
                          className={`p-3 rounded-xl border ${
                            alert.alert_level === 'critical' ? 'bg-error/10 border-error/30' :
                            alert.alert_level === 'warning' ? 'bg-warning/10 border-warning/30' :
                            'bg-info/10 border-info/30'
                          }`}
                          data-testid={`alert-${alert.item_id}`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-semibold text-sm">{alert.item_name}</p>
                              <p className="text-xs text-muted-foreground">{alert.category} • {alert.unit}</p>
                            </div>
                            <Badge 
                              variant={alert.alert_level === 'critical' ? 'destructive' : alert.alert_level === 'warning' ? 'warning' : 'secondary'}
                              className={`text-xs ${
                                alert.alert_level === 'critical' ? 'bg-error' :
                                alert.alert_level === 'warning' ? 'bg-warning text-white' :
                                'bg-info text-white'
                              }`}
                            >
                              {alert.alert_level.toUpperCase()}
                            </Badge>
                          </div>
                          
                          <p className={`text-xs font-medium mb-2 ${
                            alert.alert_level === 'critical' ? 'text-error' :
                            alert.alert_level === 'warning' ? 'text-warning' :
                            'text-info'
                          }`}>
                            {alert.alert_reason}
                          </p>
                          
                          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-center text-[10px]">
                            <div className="p-1.5 bg-white/70 rounded border">
                              <p className="text-muted-foreground">Current</p>
                              <p className="font-bold">{alert.current_balance}</p>
                            </div>
                            <div className="p-1.5 bg-white/70 rounded border">
                              <p className="text-muted-foreground">Daily Avg</p>
                              <p className="font-bold">{alert.avg_daily_usage}</p>
                            </div>
                            <div className="p-1.5 bg-white/70 rounded border">
                              <p className="text-muted-foreground">Days Left</p>
                              <p className="font-bold">{alert.days_until_stockout || '∞'}</p>
                            </div>
                            <div className="p-1.5 bg-success/10 rounded border border-success/30">
                              <p className="text-muted-foreground">Suggest Order</p>
                              <p className="font-bold text-success">{alert.suggested_order_qty}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {smartAlerts.alerts?.length === 0 && (
                        <div className="text-center py-8 text-muted-foreground">
                          <Package size={48} className="mx-auto mb-2 opacity-30" />
                          <p>No stock alerts - all items are well stocked!</p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* CURRENT STOCK BALANCE */}
          <TabsContent value="balance">
            {!branchFilter && balance.some((b) => b.by_branch?.length) && (() => {
              const agg = {};
              balance.forEach((b) => (b.by_branch || []).forEach((x) => {
                const k = x.branch_id || '__none__';
                agg[k] = agg[k] || { items: 0, value: 0 };
                if (x.balance > 0) agg[k].items += 1;
                agg[k].value += x.value || 0;
              }));
              return (
                <div className="mb-3 rounded-xl border border-border bg-muted/30 px-3 py-2" data-testid="stock-branch-split">
                  <p className="text-[11px] font-semibold text-muted-foreground mb-1">Company total = sum of each branch's own stock (quantities are never moved between branches). Pick a branch above for its exact quantities.</p>
                  {(() => {
                    const sumBr = Object.values(agg).reduce((t, v) => t + v.value, 0);
                    const diff = blendedValue - sumBr;
                    return (
                      <p className="text-[11px] text-muted-foreground mb-1" data-testid="stock-value-reconcile">
                        Stock Value = sum of branch values at each branch's own average cost: <b>{cur()} {sumBr.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b>
                        {Math.abs(diff) >= 0.01 && <> · for reference, one company-wide average cost would give {cur()} {blendedValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (costing basis only — quantities are identical)</>}
                      </p>
                    );
                  })()}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    {Object.entries(agg).map(([k, v]) => (
                      <span key={k} data-testid={`stock-branch-split-${k}`}>
                        <b>{k === '__none__' ? 'Unassigned' : (branches.find((br) => br.id === k)?.name || 'Unknown branch')}</b>: {v.items} items in stock · {cur()} {v.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}
            <Card className="border-border">
              <CardContent className="pt-4">
                {/* Mobile card view */}
                <div className="sm:hidden space-y-2">
                  {balance.map(b => (
                    <div key={b.item_id} className={`p-3 border rounded-xl ${b.low_stock ? 'bg-error/10 border-error/30' : 'bg-white'}`} data-testid={`stock-card-${b.item_id}`}>
                      <div className="flex justify-between items-start mb-1.5">
                        <div>
                          <p className="text-sm font-bold">{b.item_name}</p>
                          <span className="text-[10px] text-muted-foreground capitalize">{b.unit}</span>
                        </div>
                        {b.low_stock ? <Badge className="bg-error/20 text-error text-[10px]">Low Stock</Badge> : <Badge className="bg-success/20 text-success text-[10px]">OK</Badge>}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 text-center text-[10px]">
                        <div className="p-1 bg-success/10 rounded"><p className="text-muted-foreground">In</p><p className="font-bold text-success">{b.stock_in}</p></div>
                        <div className="p-1 bg-error/10 rounded"><p className="text-muted-foreground">Used</p><p className="font-bold text-error">{b.stock_used}</p></div>
                        <div className="p-1 bg-muted/50 rounded"><p className="text-muted-foreground">Bal</p><p className="font-bold">{b.balance}</p></div>
                        <div className="p-1 bg-info/10 rounded"><p className="text-muted-foreground">Value</p><p className="font-bold text-info">{cur()} {rowValue(b).toFixed(0)}</p></div>
                      </div>
                    </div>
                  ))}
                  {balance.length === 0 && <p className="text-center text-muted-foreground py-8">No stock data yet</p>}
                </div>
                {/* Desktop table */}
                <div className="hidden sm:block">
                <VirtualizedTable
                  data={balance}
                  maxHeight={550}
                  rowHeight={48}
                  emptyMessage="No stock data yet. Add items and stock entries to get started."
                  columns={[
                    {
                      key: 'item_name', header: 'Item', width: '20%',
                      render: (val) => <span className="text-sm font-medium">{val}</span>
                    },
                    {
                      key: 'unit', header: 'Unit', width: '8%',
                      render: (val) => <span className="text-sm capitalize">{val}</span>
                    },
                    {
                      key: 'stock_in', header: 'Stock In', width: '10%', align: 'right',
                      render: (val) => <span className="text-sm text-success">{val}</span>
                    },
                    {
                      key: 'stock_used', header: 'Used', width: '10%', align: 'right',
                      render: (val) => <span className="text-sm text-error">{val}</span>
                    },
                    {
                      key: 'balance', header: 'Balance', width: '10%', align: 'right',
                      render: (val) => <span className="text-sm font-bold">{val}</span>
                    },
                    {
                      key: 'avg_cost', header: 'Avg Cost', width: '12%', align: 'right',
                      render: (val) => <span className="text-sm">{cur()} {val}</span>
                    },
                    {
                      key: 'stock_value', header: 'Value', width: '15%', align: 'right',
                      render: (val, row) => <span className="text-sm font-medium">{cur()} {rowValue(row).toFixed(2)}</span>
                    },
                    {
                      key: 'low_stock', header: 'Status', width: '12%', align: 'center',
                      render: (val) => val 
                        ? <Badge className="bg-error/20 text-error text-[10px]">Low Stock</Badge> 
                        : <Badge className="bg-success/20 text-success text-[10px]">OK</Badge>
                    },
                  ]}
                  data-testid="stock-balance-table"
                />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* STOCK IN LOG */}
          <TabsContent value="entries">
            <Card className="border-border">
              <CardContent className="pt-4">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b">
                      <th className="text-left p-3 text-sm font-medium">Date</th>
                      <th className="text-left p-3 text-sm font-medium">Item</th>
                      <th className="text-left p-3 text-sm font-medium">Branch</th>
                      <th className="text-right p-3 text-sm font-medium">Qty</th>
                      <th className="text-right p-3 text-sm font-medium">Unit Cost</th>
                      <th className="text-right p-3 text-sm font-medium">Total</th>
                      <th className="text-left p-3 text-sm font-medium">Source</th>
                    </tr></thead>
                    <tbody>
                      {entries.map(e => (
                        <tr key={e.id} className="border-b hover:bg-muted/50">
                          <td className="p-3 text-sm">{format(new Date(e.date), 'MMM dd, yyyy')}</td>
                          <td className="p-3 text-sm font-medium">{e.item_name}</td>
                          <td className="p-3 text-sm">{branches.find(b => b.id === e.branch_id)?.name || '-'}</td>
                          <td className="p-3 text-sm text-right">{e.quantity}</td>
                          <td className="p-3 text-sm text-right">{cur()} {e.unit_cost?.toFixed(2)}</td>
                          <td className="p-3 text-sm text-right font-medium">{cur()} {(e.quantity * (e.unit_cost || 0)).toFixed(2)}</td>
                          <td className="p-3"><Badge variant="outline" className="capitalize">{e.source}</Badge></td>
                        </tr>
                      ))}
                      {entries.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No stock entries yet</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* USAGE LOG */}
          <TabsContent value="usage">
            <Card className="border-border">
              <CardContent className="pt-4">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b">
                      <th className="text-left p-3 text-sm font-medium">Date</th>
                      <th className="text-left p-3 text-sm font-medium">Item</th>
                      <th className="text-left p-3 text-sm font-medium">Branch</th>
                      <th className="text-right p-3 text-sm font-medium">Qty Used</th>
                      <th className="text-left p-3 text-sm font-medium">Used By</th>
                      <th className="text-left p-3 text-sm font-medium">Notes</th>
                    </tr></thead>
                    <tbody>
                      {usage.map(u => (
                        <tr key={u.id} className="border-b hover:bg-muted/50">
                          <td className="p-3 text-sm">{format(new Date(u.date), 'MMM dd, yyyy')}</td>
                          <td className="p-3 text-sm font-medium">{u.item_name}</td>
                          <td className="p-3 text-sm">{branches.find(b => b.id === u.branch_id)?.name || '-'}</td>
                          <td className="p-3 text-sm text-right font-bold text-error">{u.quantity}</td>
                          <td className="p-3 text-sm">{u.used_by}</td>
                          <td className="p-3 text-sm text-muted-foreground">{u.notes || '-'}</td>
                        </tr>
                      ))}
                      {usage.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No usage recorded yet</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ITEM MASTER */}
          <TabsContent value="items">
            <Card className="border-border">
              <CardContent className="pt-4">
                {/* Batch barcode actions */}
                {selectedItemsForBatch.length > 0 && (
                  <div className="flex items-center gap-2 mb-4 p-3 bg-info/10 rounded-xl border border-info/30">
                    <span className="text-sm font-medium text-info">{selectedItemsForBatch.length} items selected</span>
                    <Button size="sm" variant="outline" className="ml-auto rounded-xl" onClick={handleBatchPrint} data-testid="batch-print-btn">
                      <Printer size={14} className="mr-1" />Print Batch Labels
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedItemsForBatch([])}>Clear</Button>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="items-table">
                    <thead><tr className="border-b">
                      <th className="p-3 w-10">
                        <input 
                          type="checkbox" 
                          className="rounded border-border"
                          checked={selectedItemsForBatch.length === items.length && items.length > 0}
                          onChange={(e) => setSelectedItemsForBatch(e.target.checked ? items.map(i => i.id) : [])}
                        />
                      </th>
                      <th className="text-left p-3 text-sm font-medium">Name</th>
                      <th className="text-left p-3 text-sm font-medium">Category</th>
                      <th className="text-left p-3 text-sm font-medium">Unit</th>
                      <th className="text-right p-3 text-sm font-medium">Cost Price</th>
                      <th className="text-right p-3 text-sm font-medium">Sale Price</th>
                      <th className="text-right p-3 text-sm font-medium">Min Level</th>
                      <th className="text-center p-3 text-sm font-medium">Auto-Reorder</th>
                      <th className="text-center p-3 text-sm font-medium">Actions</th>
                    </tr></thead>
                    <tbody>
                      {items.filter(i => categoryFilter === 'all' || i.category === categoryFilter).map(item => (
                        <tr key={item.id} className={`border-b hover:bg-muted/50 ${selectedItemsForBatch.includes(item.id) ? 'bg-info/10' : ''}`}>
                          <td className="p-3">
                            <input 
                              type="checkbox" 
                              className="rounded border-border"
                              checked={selectedItemsForBatch.includes(item.id)}
                              onChange={() => toggleItemSelection(item.id)}
                            />
                          </td>
                          <td className="p-3 text-sm font-medium">{item.name}</td>
                          <td className="p-3 text-sm">{item.category || '-'}</td>
                          <td className="p-3 text-sm capitalize">{item.unit || 'piece'}</td>
                          <td className="p-3 text-sm text-right">{cur()} {(item.cost_price || 0).toFixed(2)}</td>
                          <td className="p-3 text-sm text-right">{cur()} {(item.unit_price || 0).toFixed(2)}</td>
                          <td className="p-3 text-sm text-right">{item.min_stock_level || 0}</td>
                          <td className="p-3 text-center">
                            {item.auto_reorder ? (
                              <Badge className="bg-success/20 text-success text-[10px]" data-testid="auto-reorder-badge">ON</Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="p-3 text-center flex items-center gap-1 justify-center">
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openSupplierNames(item)} title="Link supplier names" data-testid={`supplier-link-btn-${item.id}`}>
                              <Link size={16} className="text-info" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => handlePreviewBarcode(item)} data-testid={`barcode-btn-${item.id}`}>
                              <Barcode size={16} className="text-orange-500" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400 hover:text-error hover:bg-error/10" onClick={() => handleDeleteItem(item.id)} data-testid={`delete-item-${item.id}`}>
                              <Trash2 size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {items.filter(i => categoryFilter === 'all' || i.category === categoryFilter).length === 0 && <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">No items</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* REPORTS */}
          <TabsContent value="reports">
            <div className="space-y-4" data-testid="stock-reports">
              {/* Summary Cards */}
              {profitReport && (
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  <Card className="border-border"><CardContent className="pt-4 pb-3">
                    <p className="text-xs text-muted-foreground">Total Consumption Cost</p>
                    <p className="text-lg font-bold font-outfit text-error">{cur()} {consumptionReport?.total_consumption_cost?.toLocaleString() || 0}</p>
                    <p className="text-[10px] text-muted-foreground">Last {reportDays} days</p>
                  </CardContent></Card>
                  <Card className="border-border"><CardContent className="pt-4 pb-3">
                    <p className="text-xs text-muted-foreground">Potential Revenue</p>
                    <p className="text-lg font-bold font-outfit text-success">{cur()} {profitReport?.total_consumed_revenue?.toLocaleString() || 0}</p>
                  </CardContent></Card>
                  <Card className="border-border"><CardContent className="pt-4 pb-3">
                    <p className="text-xs text-muted-foreground">Gross Profit</p>
                    <p className="text-lg font-bold font-outfit text-info">{cur()} {profitReport?.total_profit?.toLocaleString() || 0}</p>
                    <p className="text-[10px] text-muted-foreground">Avg Margin: {profitReport?.avg_margin_pct || 0}%</p>
                  </CardContent></Card>
                  <Card className="border-border"><CardContent className="pt-4 pb-3">
                    <p className="text-xs text-muted-foreground">Wastage Loss</p>
                    <p className="text-lg font-bold font-outfit text-warning">{cur()} {wastageReport?.total_waste_cost?.toLocaleString() || 0}</p>
                    <p className="text-[10px] text-muted-foreground">{wastageReport?.total_waste_entries || 0} entries</p>
                  </CardContent></Card>
                </div>
              )}

              {/* Consumption Chart */}
              {consumptionReport?.daily_trend?.length > 0 && (
                <Card className="border-border">
                  <CardHeader className="py-3"><CardTitle className="text-sm font-outfit">Daily Consumption Trend</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={consumptionReport.daily_trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Bar dataKey="total" fill="#f97316" radius={[4, 4, 0, 0]} name="Units Used" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Profitability Table */}
              {profitReport?.items?.length > 0 && (
                <Card className="border-border">
                  <CardHeader className="py-3"><CardTitle className="text-sm font-outfit">Item Profitability</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="profitability-table">
                        <thead><tr className="bg-muted/50 border-y border-border">
                          <th className="text-left p-2 font-medium text-xs">Item</th>
                          <th className="text-right p-2 font-medium text-xs">Avg Cost</th>
                          <th className="text-right p-2 font-medium text-xs">Sale Price</th>
                          <th className="text-right p-2 font-medium text-xs">Margin %</th>
                          <th className="text-right p-2 font-medium text-xs">Consumed</th>
                          <th className="text-right p-2 font-medium text-xs">Cost</th>
                          <th className="text-right p-2 font-medium text-xs">Revenue</th>
                          <th className="text-right p-2 font-medium text-xs">Profit</th>
                        </tr></thead>
                        <tbody>
                          {profitReport.items.map((item, i) => (
                            <tr key={i} className="border-b border-stone-50 hover:bg-muted/50">
                              <td className="p-2 font-medium">{item.item_name} <span className="text-xs text-muted-foreground">{item.unit}</span></td>
                              <td className="p-2 text-right font-mono text-xs">{item.avg_cost?.toFixed(2)}</td>
                              <td className="p-2 text-right font-mono text-xs">{item.sale_price?.toFixed(2)}</td>
                              <td className="p-2 text-right"><Badge variant="outline" className={`text-[10px] ${item.margin_pct > 30 ? 'border-success/30 text-success' : item.margin_pct > 0 ? 'border-warning/30 text-warning' : 'border-error/30 text-error'}`}>{item.margin_pct}%</Badge></td>
                              <td className="p-2 text-right font-mono text-xs">{item.qty_consumed}</td>
                              <td className="p-2 text-right font-mono text-xs text-error">{item.consumed_cost?.toLocaleString()}</td>
                              <td className="p-2 text-right font-mono text-xs text-success">{item.consumed_revenue?.toLocaleString()}</td>
                              <td className={`p-2 text-right font-mono text-xs font-medium ${item.consumed_profit >= 0 ? 'text-success' : 'text-error'}`}>{item.consumed_profit?.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Consumption Table */}
              {consumptionReport?.items?.length > 0 && (
                <Card className="border-border">
                  <CardHeader className="py-3"><CardTitle className="text-sm font-outfit">Consumption Analysis (Last {reportDays} days)</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="consumption-table">
                        <thead><tr className="bg-muted/50 border-y border-border">
                          <th className="text-left p-2 font-medium text-xs">Item</th>
                          <th className="text-right p-2 font-medium text-xs">Total Used</th>
                          <th className="text-right p-2 font-medium text-xs">Daily Avg</th>
                          <th className="text-right p-2 font-medium text-xs">Active Days</th>
                          <th className="text-right p-2 font-medium text-xs">Cost/Unit</th>
                          <th className="text-right p-2 font-medium text-xs">Total Cost</th>
                        </tr></thead>
                        <tbody>
                          {consumptionReport.items.map((item, i) => (
                            <tr key={i} className="border-b border-stone-50 hover:bg-muted/50">
                              <td className="p-2 font-medium">{item.item_name} <span className="text-xs text-muted-foreground">{item.unit}</span></td>
                              <td className="p-2 text-right font-mono text-xs">{item.total_used}</td>
                              <td className="p-2 text-right font-mono text-xs">{item.daily_avg}</td>
                              <td className="p-2 text-right text-xs">{item.active_days}</td>
                              <td className="p-2 text-right font-mono text-xs">{item.cost_per_unit?.toFixed(2)}</td>
                              <td className="p-2 text-right font-mono text-xs font-medium">{item.total_cost?.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Wastage Report — detailed rows + totals + filters + export */}
              {wastageReport && (
                <Card className="border-border" data-testid="wastage-report-card">
                  <CardHeader className="py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <CardTitle className="text-sm font-outfit flex items-center gap-2"><Trash2 size={15} className="text-error" /> Wastage Report</CardTitle>
                      <div className="flex flex-wrap items-center gap-2">
                        <DateRangeFilter value={wasteRange} onChange={setWasteRange} testid="waste-date-range" />
                        <BranchMultiSelect value={wasteBranchIds} onChange={setWasteBranchIds} branches={branches} width="w-48" testid="waste-branch" />
                        <ExportButtons branchIds={branchFilter && branchFilter !== 'all' ? [branchFilter] : []}
                          dataType="wastage"
                          label="Wastage Report"
                          startDate={wastageReport.start}
                          endDate={wastageReport.end}
                          filters={{ branch_ids: wasteBranchIds }}
                        />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Totals */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 rounded-xl bg-error/5 border border-error/20 text-center" data-testid="waste-total-qty">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Waste Qty</p>
                        <p className="text-lg font-bold font-outfit text-error">{wastageReport.total_waste_qty?.toLocaleString() || 0}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-error/5 border border-error/20 text-center" data-testid="waste-total-value">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Waste Value</p>
                        <p className="text-lg font-bold font-outfit text-error">{cur()} {wastageReport.total_waste_value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 0}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-muted/40 border border-border text-center" data-testid="waste-total-records">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Records</p>
                        <p className="text-lg font-bold font-outfit">{wastageReport.row_count || 0}</p>
                      </div>
                    </div>

                    {/* Detailed rows */}
                    <div className="overflow-x-auto border border-border rounded-xl">
                      <table className="w-full text-sm" data-testid="wastage-rows-table">
                        <thead><tr className="bg-muted/50 border-b border-border">
                          <th className="text-left p-2 font-medium text-xs">Date</th>
                          <th className="text-left p-2 font-medium text-xs">Branch</th>
                          <th className="text-left p-2 font-medium text-xs">Item</th>
                          <th className="text-right p-2 font-medium text-xs">Qty</th>
                          <th className="text-left p-2 font-medium text-xs">Reason</th>
                          <th className="text-right p-2 font-medium text-xs">Cost/Value</th>
                          <th className="text-left p-2 font-medium text-xs">Recorded By</th>
                        </tr></thead>
                        <tbody>
                          {(wastageReport.rows || []).map((r, i) => (
                            <tr key={i} className="border-b border-stone-50 hover:bg-muted/50" data-testid={`wastage-row-${i}`}>
                              <td className="p-2 text-xs whitespace-nowrap">{String(r.date || '').slice(0, 10)}</td>
                              <td className="p-2 text-xs">{r.branch_name || '—'}</td>
                              <td className="p-2 font-medium text-xs">{r.item_name}{r.reason === 'other' && r.notes ? <span className="text-muted-foreground"> · {r.notes}</span> : null}</td>
                              <td className="p-2 text-right font-mono text-xs text-error">{r.qty} {r.unit}</td>
                              <td className="p-2 text-xs"><Badge variant="outline" className="text-[10px] border-error/30 text-error">{reasonLabel(r.reason)}</Badge></td>
                              <td className="p-2 text-right font-mono text-xs font-medium text-error">{cur()} {r.cost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td className="p-2 text-xs text-muted-foreground">{r.recorded_by || '—'}</td>
                            </tr>
                          ))}
                          {!(wastageReport.rows || []).length && (
                            <tr><td colSpan={7} className="p-6 text-center text-xs text-muted-foreground">No waste recorded for this period/branch.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* By-item summary */}
                    {wastageReport.items?.length > 0 && (
                      <div className="overflow-x-auto border border-border rounded-xl">
                        <table className="w-full text-sm" data-testid="wastage-table">
                          <thead><tr className="bg-muted/50 border-b border-border">
                            <th className="text-left p-2 font-medium text-xs">Item</th>
                            <th className="text-right p-2 font-medium text-xs">Wasted</th>
                            <th className="text-right p-2 font-medium text-xs">Normal Use</th>
                            <th className="text-right p-2 font-medium text-xs">Waste %</th>
                            <th className="text-right p-2 font-medium text-xs">Waste Cost</th>
                          </tr></thead>
                          <tbody>
                            {wastageReport.items.map((item, i) => (
                              <tr key={i} className="border-b border-stone-50 hover:bg-muted/50">
                                <td className="p-2 font-medium">{item.item_name}</td>
                                <td className="p-2 text-right font-mono text-xs text-error">{item.waste_qty} {item.unit}</td>
                                <td className="p-2 text-right font-mono text-xs">{item.normal_qty} {item.unit}</td>
                                <td className="p-2 text-right"><Badge variant="outline" className={`text-[10px] ${item.waste_pct > 10 ? 'border-error/30 text-error' : 'border-warning/30 text-warning'}`}>{item.waste_pct}%</Badge></td>
                                <td className="p-2 text-right font-mono text-xs font-medium text-error">{cur()} {item.waste_cost?.toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {!consumptionReport && !profitReport && (
                <Card className="border-border border-dashed">
                  <CardContent className="py-12 text-center">
                    <BarChart3 size={36} className="mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">Click the Reports tab to load stock analytics</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* PRODUCTION & VARIANCE */}
          <TabsContent value="production">
            <div className="space-y-4" data-testid="stock-production-report">
              {/* Date range picker */}
              <div className="flex flex-wrap items-end gap-2 bg-muted/30 border border-border rounded-lg p-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">From</label>
                  <input type="date" value={prodStart} onChange={(e) => setProdStart(e.target.value)} className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="prod-start-date" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">To</label>
                  <input type="date" value={prodEnd} onChange={(e) => setProdEnd(e.target.value)} className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="prod-end-date" />
                </div>
                <Button size="sm" onClick={loadProduction} data-testid="prod-apply-btn" className="h-9">Apply</Button>
                <div className="flex gap-1 ml-auto">
                  {[
                    { label: '7d', days: 7 }, { label: '30d', days: 30 }, { label: '90d', days: 90 },
                  ].map(p => (
                    <Button key={p.label} size="sm" variant="outline" className="h-9 text-xs"
                      onClick={() => {
                        const e = new Date(); const sdt = new Date(); sdt.setDate(sdt.getDate() - p.days);
                        const sd = sdt.toISOString().slice(0, 10); const ed = e.toISOString().slice(0, 10);
                        setProdStart(sd); setProdEnd(ed);
                        const br = branchFilter || undefined;
                        api.get('/stock/report/food-production', { params: { branch_id: br, start_date: sd, end_date: ed } })
                          .then(r => setFoodProduction(r.data)).catch(() => toast.error('Failed to load production report'));
                      }}>{p.label}</Button>
                  ))}
                </div>
              </div>
              {!foodProduction ? (
                <Card className="border-border border-dashed">
                  <CardContent className="py-12 text-center">
                    <BarChart3 size={36} className="mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">Click the Production & Variance tab to load the report</p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Summary cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Card className="border-border"><CardContent className="pt-4 pb-3">
                      <p className="text-xs text-muted-foreground">Theoretical Cost</p>
                      <p className="text-lg font-bold font-outfit text-info" data-testid="prod-theoretical-cost">{cur()} {foodProduction.variance_summary?.theoretical_cost?.toLocaleString() || 0}</p>
                      <p className="text-[10px] text-muted-foreground">Recipe-expected from sales</p>
                    </CardContent></Card>
                    <Card className="border-border"><CardContent className="pt-4 pb-3">
                      <p className="text-xs text-muted-foreground">Actual Cost</p>
                      <p className="text-lg font-bold font-outfit text-foreground" data-testid="prod-actual-cost">{cur()} {foodProduction.variance_summary?.actual_cost?.toLocaleString() || 0}</p>
                      <p className="text-[10px] text-muted-foreground">All recorded usage</p>
                    </CardContent></Card>
                    <Card className="border-border"><CardContent className="pt-4 pb-3">
                      <p className="text-xs text-muted-foreground">Variance Cost</p>
                      <p className={`text-lg font-bold font-outfit ${(foodProduction.variance_summary?.variance_cost || 0) > 0 ? 'text-error' : 'text-success'}`} data-testid="prod-variance-cost">{cur()} {foodProduction.variance_summary?.variance_cost?.toLocaleString() || 0}</p>
                      <p className="text-[10px] text-muted-foreground">{foodProduction.variance_summary?.items_over || 0} over · {foodProduction.variance_summary?.items_under || 0} under</p>
                    </CardContent></Card>
                    <Card className="border-border"><CardContent className="pt-4 pb-3">
                      <p className="text-xs text-muted-foreground">Production</p>
                      <p className="text-lg font-bold font-outfit text-success">{foodProduction.production_summary?.total_units?.toLocaleString() || 0} units</p>
                      <p className="text-[10px] text-muted-foreground">{foodProduction.production_summary?.total_batches || 0} batches · {cur()} {foodProduction.production_summary?.total_ingredient_cost?.toLocaleString() || 0}</p>
                    </CardContent></Card>
                  </div>

                  {!foodProduction.has_recipes && (
                    <div className="text-xs text-muted-foreground bg-warning/10 border border-warning/30 rounded-lg px-3 py-2">
                      No recipes linked to menu items yet — link recipes on the Recipes page so POS sales can be matched to ingredients for variance.
                    </div>
                  )}

                  {/* Variance table */}
                  {foodProduction.variance?.length > 0 && (
                    <Card className="border-border">
                      <CardHeader className="py-3"><CardTitle className="text-sm font-outfit">Recipe vs Actual Variance</CardTitle></CardHeader>
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm" data-testid="variance-table">
                            <thead><tr className="bg-muted/50 border-y border-border">
                              <th className="text-left p-2 font-medium text-xs">Ingredient</th>
                              <th className="text-right p-2 font-medium text-xs">Theoretical</th>
                              <th className="text-right p-2 font-medium text-xs">Actual</th>
                              <th className="text-right p-2 font-medium text-xs">Variance</th>
                              <th className="text-right p-2 font-medium text-xs">Var %</th>
                              <th className="text-right p-2 font-medium text-xs">Var Cost</th>
                            </tr></thead>
                            <tbody>
                              {foodProduction.variance.map((v, i) => (
                                <tr key={i} className="border-b border-stone-50 hover:bg-muted/50" data-testid={`variance-row-${i}`}>
                                  <td className="p-2 font-medium">{v.item_name} <span className="text-xs text-muted-foreground">{v.unit}</span></td>
                                  <td className="p-2 text-right font-mono text-xs">{v.theoretical_qty}</td>
                                  <td className="p-2 text-right font-mono text-xs">{v.actual_qty}</td>
                                  <td className={`p-2 text-right font-mono text-xs font-medium ${v.variance_qty > 0 ? 'text-error' : v.variance_qty < 0 ? 'text-success' : ''}`}>{v.variance_qty > 0 ? '+' : ''}{v.variance_qty}</td>
                                  <td className="p-2 text-right"><Badge variant="outline" className={`text-[10px] ${Math.abs(v.variance_pct) > 10 ? 'border-error/30 text-error' : 'border-warning/30 text-warning'}`}>{v.variance_pct}%</Badge></td>
                                  <td className={`p-2 text-right font-mono text-xs font-medium ${v.variance_cost > 0 ? 'text-error' : 'text-success'}`}>{cur()} {v.variance_cost?.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Daily production table */}
                  {foodProduction.daily_production?.length > 0 && (
                    <Card className="border-border">
                      <CardHeader className="py-3"><CardTitle className="text-sm font-outfit">Daily Production</CardTitle></CardHeader>
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm" data-testid="daily-production-table">
                            <thead><tr className="bg-muted/50 border-y border-border">
                              <th className="text-left p-2 font-medium text-xs">Date</th>
                              <th className="text-right p-2 font-medium text-xs">Batches</th>
                              <th className="text-right p-2 font-medium text-xs">Units Produced</th>
                              <th className="text-left p-2 font-medium text-xs">Recipes</th>
                              <th className="text-right p-2 font-medium text-xs">Ingredient Cost</th>
                            </tr></thead>
                            <tbody>
                              {foodProduction.daily_production.map((d, i) => (
                                <tr key={i} className="border-b border-stone-50 hover:bg-muted/50" data-testid={`production-row-${i}`}>
                                  <td className="p-2 font-medium">{d.date}</td>
                                  <td className="p-2 text-right font-mono text-xs">{d.batches}</td>
                                  <td className="p-2 text-right font-mono text-xs">{d.units_produced}</td>
                                  <td className="p-2 text-xs text-muted-foreground">{(d.recipes || []).map(r => `${r.name} ×${r.qty}`).join(', ')}</td>
                                  <td className="p-2 text-right font-mono text-xs">{cur()} {d.ingredient_cost?.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {foodProduction.variance?.length === 0 && foodProduction.daily_production?.length === 0 && (
                    <Card className="border-border border-dashed"><CardContent className="py-10 text-center text-muted-foreground text-sm">No production or usage activity in this period.</CardContent></Card>
                  )}
                </>
              )}
            </div>
          </TabsContent>

        </Tabs>

        {/* ADD ITEM DIALOG */}
        <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
          <DialogContent data-testid="add-item-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Add New Item</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Item Name *</Label><Input value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} placeholder="e.g. Chicken Breast" data-testid="item-name-input" /></div>
                <div><Label>Category</Label>
                  <div className="flex gap-1">
                    <Select value={newItem.category || "none"} onValueChange={(v) => setNewItem({ ...newItem, category: v === "none" ? "" : v })}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Category</SelectItem>
                        {stockCategories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" className="px-2" onClick={() => setShowNewCategory(true)} title="Add category"><Plus size={14} /></Button>
                  </div>
                </div>
                <div><Label>Cost Price (SR)</Label><Input type="number" step="0.01" value={newItem.cost_price} onChange={(e) => setNewItem({ ...newItem, cost_price: e.target.value })} placeholder="0.00" /></div>
                <div><Label>Sale Price (SR)</Label><Input type="number" step="0.01" value={newItem.unit_price} onChange={(e) => setNewItem({ ...newItem, unit_price: e.target.value })} placeholder="0.00" /></div>
                <div><Label>Unit</Label>
                  <div className="flex gap-1">
                    <Select value={newItem.unit || "piece"} onValueChange={(v) => setNewItem({ ...newItem, unit: v })}>
                      <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {stockUnits.map(u => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}
                        {stockUnits.length === 0 && <>
                          <SelectItem value="piece">Piece</SelectItem>
                          <SelectItem value="kg">Kg</SelectItem>
                          <SelectItem value="box">Box</SelectItem>
                        </>}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" className="px-2" onClick={() => setShowNewUnit(true)} title="Add unit"><Plus size={14} /></Button>
                  </div>
                </div>
                <div><Label>Min Stock Level</Label><Input type="number" value={newItem.min_stock_level} onChange={(e) => setNewItem({ ...newItem, min_stock_level: e.target.value })} placeholder="0" /></div>
                <div><Label>Reorder Quantity</Label><Input type="number" value={newItem.reorder_quantity} onChange={(e) => setNewItem({ ...newItem, reorder_quantity: e.target.value })} placeholder="Auto-calculated if 0" data-testid="reorder-qty-input" /></div>
                <div className="flex items-center gap-3 col-span-2">
                  <input type="checkbox" id="auto_reorder" checked={newItem.auto_reorder} onChange={(e) => setNewItem({ ...newItem, auto_reorder: e.target.checked })} className="w-4 h-4 rounded border-gray-300" data-testid="auto-reorder-toggle" />
                  <Label htmlFor="auto_reorder" className="cursor-pointer">Enable Auto-Reorder (auto-create PO when below min stock)</Label>
                </div>
                {/* iter880 — Pack conversions (JSON-free). Lets operators say
                    "1 sack = 25 kg" or "1 box = 24 pieces" without editing JSON.
                    Each entry: name + factor_to_base (multiplier converting to the item's base unit). */}
                <div className="col-span-2 border rounded-lg p-3 bg-slate-50/40" data-testid="pack-units-section">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-semibold flex items-center gap-1">Pack conversions
                      <span className="text-[10px] font-normal text-muted-foreground">(optional — e.g. 1 box = 24 pieces)</span>
                    </Label>
                    <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]"
                      onClick={() => setNewItem({ ...newItem, pack_units: [...(newItem.pack_units || []), { name: '', factor_to_base: '' }] })}
                      data-testid="add-pack-unit-btn">
                      <Plus size={12} className="mr-1" /> Add pack unit
                    </Button>
                  </div>
                  {(newItem.pack_units || []).length === 0 ? (
                    <p className="text-[10px] text-muted-foreground italic">No pack units yet. Add one to let recipes use alternate units (e.g. buy in &quot;sack&quot; but stock in &quot;gram&quot;).</p>
                  ) : (
                    <div className="space-y-1.5">
                      {(newItem.pack_units || []).map((pu, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs" data-testid={`pack-unit-row-${idx}`}>
                          <Input placeholder="e.g. box"
                            value={pu.name}
                            onChange={(e) => {
                              const next = [...(newItem.pack_units || [])];
                              next[idx] = { ...next[idx], name: e.target.value };
                              setNewItem({ ...newItem, pack_units: next });
                            }}
                            className="h-8 flex-1"
                            data-testid={`pack-unit-name-${idx}`}
                          />
                          <span className="text-muted-foreground">= 1</span>
                          <Input type="number" step="any" placeholder="24"
                            value={pu.factor_to_base}
                            onChange={(e) => {
                              const next = [...(newItem.pack_units || [])];
                              next[idx] = { ...next[idx], factor_to_base: e.target.value };
                              setNewItem({ ...newItem, pack_units: next });
                            }}
                            className="h-8 w-24"
                            data-testid={`pack-unit-factor-${idx}`}
                          />
                          <span className="text-muted-foreground text-[10px]">{newItem.unit || 'piece'}</span>
                          <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-rose-500"
                            onClick={() => setNewItem({ ...newItem, pack_units: (newItem.pack_units || []).filter((_, i) => i !== idx) })}
                            data-testid={`pack-unit-remove-${idx}`}
                          ><X size={12} /></Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <Button className="rounded-xl w-full" onClick={handleAddItem} data-testid="save-item-btn">Save Item</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* STOCK IN DIALOG */}
        <Dialog open={showStockIn} onOpenChange={setShowStockIn}>
          <DialogContent data-testid="stock-in-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Add Stock</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Item *</Label>
                  <Select value={stockInData.item_id || "none"} onValueChange={(v) => setStockInData({ ...stockInData, item_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="stock-item-select"><SelectValue placeholder="Select item" /></SelectTrigger>
                    <SelectContent>{items.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Branch *</Label>
                  <Select value={stockInData.branch_id || "none"} onValueChange={(v) => setStockInData({ ...stockInData, branch_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="stock-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Quantity *</Label><Input type="number" step="0.01" value={stockInData.quantity} onChange={(e) => setStockInData({ ...stockInData, quantity: e.target.value })} placeholder="0" data-testid="stock-qty-input" /></div>
                <div><Label>Unit Cost (SR)</Label><Input type="number" step="0.01" value={stockInData.unit_cost} onChange={(e) => setStockInData({ ...stockInData, unit_cost: e.target.value })} placeholder="0.00" /></div>
                <div><Label>Supplier</Label>
                  <Select value={stockInData.supplier_id || "none"} onValueChange={(v) => setStockInData({ ...stockInData, supplier_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">No Supplier</SelectItem>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Date</Label><Input type="date" value={stockInData.date} onChange={(e) => setStockInData({ ...stockInData, date: e.target.value })} /></div>
              </div>
              <div><Label>Notes</Label><Input value={stockInData.notes} onChange={(e) => setStockInData({ ...stockInData, notes: e.target.value })} placeholder="Optional" /></div>
              <Button className="rounded-xl w-full" onClick={handleStockIn} data-testid="save-stock-btn">Add Stock</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* SCAN INVOICE DIALOG */}
        <Dialog open={showScanDialog} onOpenChange={setShowScanDialog}>
          <DialogContent className="max-w-2xl" data-testid="scan-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Scan Supplier Invoice</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">Take a photo or upload an image of the supplier invoice. AI will extract all items automatically.</p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Branch *</Label>
                  <Select value={stockInData.branch_id || "none"} onValueChange={(v) => setStockInData({ ...stockInData, branch_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Supplier</Label>
                  <Select value={stockInData.supplier_id || "none"} onValueChange={(v) => setStockInData({ ...stockInData, supplier_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">No Supplier</SelectItem>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="border-2 border-dashed border-border rounded-xl p-6 text-center">
                <input type="file" accept="image/*" capture="environment" onChange={handleScanInvoice} className="hidden" id="invoice-upload" data-testid="invoice-file-input" />
                <label htmlFor="invoice-upload" className="cursor-pointer">
                  {scanning ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 size={32} className="animate-spin text-primary" />
                      <p className="text-sm font-medium">Scanning invoice...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Camera size={32} className="text-muted-foreground" />
                      <p className="text-sm font-medium">Tap to take photo or upload invoice image</p>
                      <p className="text-xs text-muted-foreground">Supports JPG, PNG, WEBP</p>
                    </div>
                  )}
                </label>
              </div>

              {scanResult && (
                <div className="space-y-3">
                  {scanResult.supplier_name && <p className="text-sm"><strong>Supplier:</strong> {scanResult.supplier_name}</p>}
                  {scanResult.invoice_number && <p className="text-sm"><strong>Invoice #:</strong> {scanResult.invoice_number}</p>}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="bg-muted/50 border-b">
                          <th className="text-left p-2">Item</th>
                          <th className="text-right p-2">Qty</th>
                          <th className="text-left p-2">Unit</th>
                          <th className="text-right p-2">Cost</th>
                          <th className="text-right p-2">Total</th>
                        </tr></thead>
                        <tbody>
                          {scanResult.items?.map((item, i) => (
                            <tr key={i} className="border-b">
                              <td className="p-2 cursor-pointer hover:text-primary" onClick={() => openSupplierNames(item)} title="Click to manage supplier names">{item.name}
                            {item.supplier_mappings_count > 0 && <span className="ml-1 text-[9px] text-primary">({item.supplier_mappings_count} suppliers)</span>}
                          </td>
                              <td className="p-2 text-right">{item.quantity}</td>
                              <td className="p-2">{item.unit || 'piece'}</td>
                              <td className="p-2 text-right">{cur()} {(item.unit_cost || 0).toFixed(2)}</td>
                              <td className="p-2 text-right font-medium">{cur()} {(item.total || 0).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {scanResult.total && <p className="text-right font-bold">Total: {cur()} {scanResult.total.toFixed(2)}</p>}
                  <div className="flex gap-2">
                    <Button className="rounded-xl flex-1" variant="outline" onClick={handleSmartMatch} data-testid="smart-match-btn">
                      <Link size={14} className="mr-2" />Smart Match to Stock
                    </Button>
                    <Button className="rounded-xl flex-1" onClick={handleImportScanned} data-testid="import-scanned-btn">
                      <Package size={14} className="mr-2" />Import {scanResult.items?.length || 0} Items
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
        <WhatsAppSendDialog open={showWhatsApp} onClose={() => setShowWhatsApp(false)} defaultType="low_stock" branches={branches} branchId={branchFilter} />
        <RecordWasteDialog
          open={showRecordWaste}
          onOpenChange={setShowRecordWaste}
          items={items}
          branches={branches}
          defaultBranchId={branchFilter}
          onRecorded={() => { fetchAll(); fetchBalance(); loadWastage(); }}
        />

        {/* BARCODE PREVIEW DIALOG */}
        <Dialog open={showBarcodePreview} onOpenChange={(open) => {
          setShowBarcodePreview(open);
          if (!open) {
            setBarcodeImageUrl('');
            setSelectedBarcodeItem(null);
          }
        }}>
          <DialogContent className="max-w-md" data-testid="barcode-preview-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit">Barcode Label</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {barcodeLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={32} className="animate-spin text-primary" />
                </div>
              ) : barcodeImageUrl ? (
                <>
                  <div className="border rounded-xl p-4 bg-white">
                    <img 
                      src={barcodeImageUrl} 
                      alt={`Barcode for ${selectedBarcodeItem?.name}`} 
                      className="w-full"
                      data-testid="barcode-image"
                    />
                  </div>
                  <div className="text-sm text-muted-foreground text-center">
                    <p className="font-medium">{selectedBarcodeItem?.name}</p>
                    <p>{cur()} {(selectedBarcodeItem?.unit_price || 0).toFixed(2)}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      className="flex-1 rounded-xl" 
                      variant="outline"
                      onClick={handlePrintBarcode}
                      data-testid="print-barcode-btn"
                    >
                      <Printer size={14} className="mr-1" />Print
                    </Button>
                    <Button 
                      className="flex-1 rounded-xl"
                      onClick={() => handleDownloadBarcode(selectedBarcodeItem?.id)}
                      data-testid="download-barcode-btn"
                    >
                      <Download size={14} className="mr-1" />Download
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>Failed to load barcode preview</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Stock Alert Settings Dialog */}
        <Dialog open={showAlertSettings} onOpenChange={setShowAlertSettings}>
          <DialogContent className="max-w-md" data-testid="stock-alert-settings-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit flex items-center gap-2"><Bell size={18} /> Stock Alert Settings</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="flex items-center justify-between">
                <Label>Enable Low Stock Alerts</Label>
                <button onClick={() => setAlertSettings(s => ({ ...s, enabled: !s.enabled }))}
                  className={`w-12 h-6 rounded-full transition-colors ${alertSettings.enabled ? 'bg-success' : 'bg-stone-300'}`}
                  data-testid="alert-enabled-toggle">
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${alertSettings.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <div>
                <Label className="text-sm">Global Threshold (units)</Label>
                <p className="text-xs text-muted-foreground mb-1">Items below this level will trigger alerts (unless item has custom threshold)</p>
                <Input type="number" value={alertSettings.global_threshold}
                  onChange={e => setAlertSettings(s => ({ ...s, global_threshold: Number(e.target.value) }))}
                  data-testid="alert-global-threshold" />
              </div>
              <div>
                <Label className="text-sm">Check Interval (minutes)</Label>
                <Select value={String(alertSettings.check_interval_minutes)} onValueChange={v => setAlertSettings(s => ({ ...s, check_interval_minutes: Number(v) }))}>
                  <SelectTrigger data-testid="alert-interval"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">Every 30 minutes</SelectItem>
                    <SelectItem value="60">Every hour</SelectItem>
                    <SelectItem value="120">Every 2 hours</SelectItem>
                    <SelectItem value="360">Every 6 hours</SelectItem>
                    <SelectItem value="720">Every 12 hours</SelectItem>
                    <SelectItem value="1440">Once daily</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Email Notifications</Label>
                  <p className="text-xs text-muted-foreground">Send email when stock is low</p>
                </div>
                <button onClick={() => setAlertSettings(s => ({ ...s, email_enabled: !s.email_enabled }))}
                  className={`w-12 h-6 rounded-full transition-colors ${alertSettings.email_enabled ? 'bg-success' : 'bg-stone-300'}`}
                  data-testid="alert-email-toggle">
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${alertSettings.email_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">Per-item thresholds can be set via the "Min Stock Level" field when adding or editing items.</p>
              <div className="pt-3 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-semibold">Auto-Reorder POs</Label>
                    <p className="text-xs text-muted-foreground">Runs every 30 min. Creates draft PO when stock is below threshold.</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={async () => {
                    try {
                      const res = await api.post('/auto-reorder/check');
                      if (res.data.created > 0) toast.success(`Auto-reorder PO created: ${res.data.po_number}`);
                      else toast.info('All stock levels OK. No PO needed.');
                    } catch { toast.error('Check failed'); }
                  }} data-testid="check-auto-reorder-btn-2">Check Now</Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAlertSettings(false)}>Cancel</Button>
              <Button onClick={saveAlertSettings} className="bg-orange-500 hover:bg-orange-600" data-testid="save-alert-settings">Save Settings</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Smart Match Dialog */}
        <Dialog open={showMatchDialog} onOpenChange={setShowMatchDialog}>
          <DialogContent className="max-w-3xl max-h-[80dvh] overflow-y-auto" data-testid="smart-match-dialog">
            <DialogHeader><DialogTitle className="font-outfit flex items-center gap-2"><Link size={18} /> Smart Match - Invoice to Stock</DialogTitle></DialogHeader>
            {matchResults && (
              <div className="space-y-3">
                <div className="flex gap-3 text-sm">
                  <Badge className="bg-success/20 text-success">Auto-matched: {matchResults.auto_matched}</Badge>
                  <Badge className="bg-warning/20 text-warning">Manual needed: {matchResults.total_invoice_items - matchResults.auto_matched}</Badge>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-muted/50 border-b">
                        <th className="text-left p-2">Invoice Item</th>
                        <th className="text-right p-2">Qty</th>
                        <th className="text-right p-2">Cost</th>
                        <th className="text-left p-2">Matched Stock Item</th>
                        <th className="text-center p-2">Score</th>
                        <th className="text-center p-2">Action</th>
                      </tr></thead>
                      <tbody>
                        {matchResults.matched_items?.map((item, i) => (
                          <tr key={i} className={`border-b ${item.best_match ? 'bg-success/10' : 'bg-warning/10'}`}>
                            <td className="p-2 font-medium">{item.name}</td>
                            <td className="p-2 text-right">{item.quantity}</td>
                            <td className="p-2 text-right">{cur()} {(item.unit_cost || 0).toFixed(2)}</td>
                            <td className="p-2">
                              {item.best_match ? (
                                <span className="text-success font-medium">{item.best_match.name}</span>
                              ) : (
                                <Select defaultValue="new" onValueChange={(v) => {
                                  const updated = {...matchResults};
                                  if (v === 'new') updated.matched_items[i].best_match = null;
                                  else updated.matched_items[i].best_match = item.matches?.find(m => m.id === v);
                                  setMatchResults({...updated});
                                }}>
                                  <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Select or create new" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="new" className="text-xs text-warning">+ Create New Item</SelectItem>
                                    {item.matches?.map(m => (
                                      <SelectItem key={m.id} value={m.id} className="text-xs">{m.name} ({m.score}%)</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </td>
                            <td className="p-2 text-center">
                              {item.best_match && <Badge className={`text-[10px] ${item.best_match.score >= 80 ? 'bg-success/20 text-success' : 'bg-warning/20 text-warning'}`}>{item.best_match.score}%</Badge>}
                            </td>
                            <td className="p-2 text-center">
                              {item.best_match ? <span className="text-[10px] text-success">✓ Linked</span> : <span className="text-[10px] text-warning">New</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <Button className="w-full rounded-xl" onClick={async () => { 
                  // Save supplier mappings for matched items
                  const mappingItems = matchResults.matched_items
                    ?.filter(i => i.best_match)
                    .map(i => ({ item_id: i.best_match.id, supplier_item_name: i.name, supplier_item_code: i.code || '', unit_cost: i.unit_cost }));
                  if (mappingItems?.length && scanResult.supplier_id) {
                    try {
                      await api.post('/stock/supplier-mappings/from-invoice', {
                        supplier_id: scanResult.supplier_id, supplier_name: scanResult.supplier_name || '',
                        items: mappingItems
                      });
                    } catch {}
                  }
                  handleImportScanned(); setShowMatchDialog(false); 
                }}>
                  <Package size={14} className="mr-2" />Import All & Save Supplier Names
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Import CSV Dialog */}
        <Dialog open={showImport} onOpenChange={setShowImport}>
          <DialogContent className="max-w-lg" data-testid="import-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Import Stock Items</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Paste CSV data: <code>Name, Category, Unit, Buy Price, Sell Price, Min Level</code></p>
              <Textarea value={importData} onChange={(e) => setImportData(e.target.value)} placeholder={"Chicken Breast, Meat, kg, 15, 25, 10\nRice 5kg, Grocery, bag, 20, 30, 5"} className="min-h-[150px] font-mono text-xs" />
              <Button className="w-full rounded-xl" onClick={handleImportCSV} disabled={!importData.trim()}>
                <Upload size={14} className="mr-2" />Import Items
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Category Dialog */}
        <Dialog open={showNewCategory} onOpenChange={setShowNewCategory}>
          <DialogContent className="max-w-sm" data-testid="add-category-dialog">
            <DialogHeader><DialogTitle>Add Stock Category</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder="e.g. Meat, Vegetables, Electronics" />
              <Button className="w-full" onClick={handleAddCategory}>Add Category</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Unit Dialog */}
        <Dialog open={showNewUnit} onOpenChange={setShowNewUnit}>
          <DialogContent className="max-w-sm" data-testid="add-unit-dialog">
            <DialogHeader><DialogTitle>Add Stock Unit</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input value={newUnitName} onChange={(e) => setNewUnitName(e.target.value)} placeholder="e.g. packet, tray, pallet" />
              <Button className="w-full" onClick={handleAddUnit}>Add Unit</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Supplier Names Dialog */}
        <Dialog open={!!showSupplierNames} onOpenChange={() => setShowSupplierNames(null)}>
          <DialogContent className="max-w-2xl" data-testid="supplier-names-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit flex items-center gap-2">
                <Link size={18} /> Supplier Names — {showSupplierNames?.name}
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground -mt-2">Map different supplier names/codes to this item. These names will be used for smart matching when scanning invoices and will appear on Purchase Orders.</p>
            
            {/* Existing Mappings */}
            {supplierMappings.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-muted/50 border-b">
                      <th className="text-left p-2">Supplier</th>
                      <th className="text-left p-2">Their Name</th>
                      <th className="text-left p-2">Their Code</th>
                      <th className="text-right p-2">Their Price</th>
                      <th className="w-10"></th>
                    </tr></thead>
                    <tbody>
                      {supplierMappings.map(m => (
                        <tr key={m.id} className="border-b">
                          <td className="p-2 font-medium text-primary">{m.supplier_name}</td>
                          <td className="p-2">{m.supplier_item_name}</td>
                          <td className="p-2 font-mono text-xs">{m.supplier_item_code || '-'}</td>
                          <td className="p-2 text-right">{m.supplier_price > 0 ? `${cur()} ${m.supplier_price}` : '-'}</td>
                          <td className="p-2"><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-error" onClick={() => deleteSupplierMapping(m.id)}><Trash2 size={12} /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground text-sm">No supplier names linked yet</div>
            )}
            
            {/* Add New Mapping */}
            <div className="border rounded-lg p-3 bg-muted/50 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground">Add Supplier Name</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px]">Supplier</Label>
                  <Select value={newMapping.supplier_id || "none"} onValueChange={(v) => setNewMapping({...newMapping, supplier_id: v === "none" ? "" : v})}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select supplier</SelectItem>
                      {suppliers.map(s => <SelectItem key={s.id} value={s.id} className="text-xs">{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">Supplier's Name for This Item</Label>
                  <Input className="h-8 text-xs" value={newMapping.supplier_item_name} onChange={(e) => setNewMapping({...newMapping, supplier_item_name: e.target.value})} placeholder="e.g. CHK-BRS-001" />
                </div>
                <div>
                  <Label className="text-[10px]">Supplier's Code (optional)</Label>
                  <Input className="h-8 text-xs" value={newMapping.supplier_item_code} onChange={(e) => setNewMapping({...newMapping, supplier_item_code: e.target.value})} placeholder="e.g. SKU-12345" />
                </div>
                <div>
                  <Label className="text-[10px]">Supplier Price (optional)</Label>
                  <Input type="number" className="h-8 text-xs" value={newMapping.supplier_price} onChange={(e) => setNewMapping({...newMapping, supplier_price: e.target.value})} placeholder="0.00" />
                </div>
              </div>
              <Button size="sm" className="w-full rounded-lg" onClick={addSupplierMapping} disabled={!newMapping.supplier_id || !newMapping.supplier_item_name}>
                <Plus size={12} className="mr-1" /> Add Supplier Name
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
